import React, { useRef, useState } from "react";
import { api, formatDetail } from "../lib/api";
import { toast } from "sonner";
import { Download, Upload, ShieldCheck, X } from "lucide-react";
import RiderSnapshots from "./RiderSnapshots";
import { useAuth } from "../lib/store";

export default function RiderBackupAdmin({ onRestored }) {
  const { user } = useAuth();
  const isPresident = !!user?.is_president;
  const [open, setOpen] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  async function download() {
    if (downloading) return;
    setDownloading(true);
    try {
      const res = await api.get("/admin/riders/backup", { responseType: "blob" });
      const name = (res.headers["content-disposition"] || "").match(/filename="([^"]+)"/)?.[1] || "glcc-riders-backup.json";
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = name; a.click();
      URL.revokeObjectURL(url);
      toast("Backup downloaded");
    } catch (e) { toast.error(formatDetail(e)); }
    finally { setDownloading(false); }
  }

  async function send(confirm) {
    if (!file || busy) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("confirm", confirm ? "true" : "false");
      const { data } = await api.post("/admin/riders/restore", fd);
      if (confirm) {
        toast(`${data.restored} rider${data.restored === 1 ? "" : "s"} restored`);
        reset();
        onRestored?.();
      } else {
        setPreview(data);
      }
    } catch (e) { toast.error(formatDetail(e)); }
    finally { setBusy(false); }
  }

  function reset() {
    setFile(null); setPreview(null);
    if (inputRef.current) inputRef.current.value = "";
  }

  function pick(e) {
    const f = e.target.files?.[0];
    setPreview(null);
    setFile(f || null);
  }

  return (
    <div className="bg-bg-secondary border border-accent-volt/20 rounded-xl p-3 mb-3" data-testid="rider-backup-block">
      <button onClick={() => setOpen((v) => !v)} className="w-full flex items-center justify-between" data-testid="rider-backup-toggle">
        <span className="flex items-center gap-2 text-[10px] uppercase font-mono-stat tracking-widest text-text-primary">
          <ShieldCheck className="w-3.5 h-3.5" /> Rider backup & restore
        </span>
        <span className="text-text-primary font-black">{open ? "−" : "+"}</span>
      </button>

      {open && (
        <div className="mt-3 space-y-3">
          <RiderSnapshots onRestored={onRestored} />
          {isPresident && (
          <>
          <button
            onClick={download}
            disabled={downloading}
            className="w-full flex items-center justify-center gap-2 bg-bg-primary border border-border text-text-primary uppercase tracking-widest text-xs font-bold py-2.5 rounded-lg disabled:opacity-50"
            data-testid="rider-backup-download-button"
          >
            <Download className="w-4 h-4" /> {downloading ? "Exporting…" : "Download backup file"}
          </button>

          <div className="border-t border-border/60 pt-3">
            <label className="block text-[10px] uppercase font-mono-stat tracking-widest text-text-muted mb-2">
              Restore from backup file
            </label>
            <input
              ref={inputRef}
              type="file"
              accept=".json,application/json"
              onChange={pick}
              className="block w-full text-xs text-text-muted file:mr-3 file:py-2 file:px-3 file:rounded-lg file:border-0 file:bg-bg-primary file:text-text-primary file:text-xs file:font-bold file:uppercase file:tracking-widest"
              data-testid="rider-restore-file-input"
            />
            {file && !preview && (
              <button
                onClick={() => send(false)}
                disabled={busy}
                className="mt-2 w-full flex items-center justify-center gap-2 bg-bg-primary border border-border text-text-primary uppercase tracking-widest text-xs font-bold py-2.5 rounded-lg disabled:opacity-50"
                data-testid="rider-restore-preview-button"
              >
                <Upload className="w-4 h-4" /> {busy ? "Checking…" : "Check backup"}
              </button>
            )}
          </div>

          {preview && (
            <div className="bg-bg-primary rounded-lg p-3 space-y-2" data-testid="rider-restore-preview">
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-bold text-text-primary">{preview.in_file} riders in file</span>
                <button onClick={reset} className="text-text-muted" data-testid="rider-restore-cancel-button"><X className="w-4 h-4" /></button>
              </div>
              <div className="text-[11px] text-text-muted" data-testid="rider-restore-counts">
                <span className="text-text-primary font-bold">{preview.restorable.length}</span> missing · will be restored<br />
                <span className="font-bold">{preview.skipped_existing.length}</span> already exist · left untouched
              </div>
              {preview.restorable.length > 0 && (
                <ul className="text-[11px] text-text-primary space-y-0.5 max-h-32 overflow-y-auto">
                  {preview.restorable.map((r) => (
                    <li key={r.email} data-testid={`rider-restore-item-${r.email}`}>
                      {r.name || r.email} <span className="text-text-muted">· {r.role || "Member"}</span>
                    </li>
                  ))}
                </ul>
              )}
              {preview.restorable.length > 0 ? (
                <button
                  onClick={() => send(true)}
                  disabled={busy}
                  className="w-full bg-accent-volt text-black uppercase tracking-widest text-xs font-black py-2.5 rounded-lg disabled:opacity-50"
                  data-testid="rider-restore-confirm-button"
                >
                  {busy ? "Restoring…" : `Restore ${preview.restorable.length} rider${preview.restorable.length === 1 ? "" : "s"}`}
                </button>
              ) : (
                <div className="text-[11px] text-text-muted italic" data-testid="rider-restore-nothing">Nothing to restore — every rider in this file already exists.</div>
              )}
            </div>
          )}
          </>
          )}
        </div>
      )}
    </div>
  );
}
